// Adapta el `data` de un plan favorito al estado del cotizador (ApiContext).
//
// `plan.data` es el payload de `/rate-engine/compute` (la salida de `transformData`):
// el nivel raíz es el request de pricing listo para reenviar, y `contextData` lleva el
// snapshot completo de MyFavoritePlanFormContext, que es de donde se hidratan los
// formularios y la tabla del dashboard.
//
// Ese formulario sintetiza campos que en una cotización real tiene que llenar el agente:
// nombre/apellido/email de relleno, una fecha de nacimiento derivada de la edad y una
// fecha de cobertura por defecto. Traspasarlos arrancaría el cotizador con datos
// ficticios y un precio calculado sobre ellos.

/**
 * El estado del formulario dentro del payload de pricing guardado.
 *
 * Único sitio que conoce la ruta `contextData`: la usan el form de templates
 * (modo edición), el dashboard y el mapper de aquí abajo.
 *
 * @param {object} planData  El `data` guardado del plan favorito.
 * @returns {object} El snapshot del formulario, o `{}` si el plan no trae nada.
 */
export const getPlanFormState = (planData) => planData?.contextData ?? {};

/**
 * Campos que sí definen "el plan" y viajan al cotizador. Es una allowlist explícita a
 * propósito: si el formulario de templates gana un campo nuevo, no se cuela aquí sin
 * que alguien decida a qué lado pertenece.
 */
const PLAN_CONFIGURATION_FIELDS = [
    // Selección de beneficios: el núcleo del template.
    'selectedBenefits',
    'selectedBenefitsNames',
    'selectedBenefitsIds',
    // Base information que sí es del plan (no del solicitante).
    'gender',
    // Condiciona el beneficio `deathcov`; los dependientes concretos se agregan en Step2.
    'addDependant',
    // Rider de trasplante de órganos.
    'organ',
    'organRider',
    'organId',
    'organRiderName',
    // Deciden qué beneficios entran en la petición de cotización (ver transformData).
    'outpationBenefitsEnabled',
    'outpationBenefitsEnabled4fases',
    'outpationBenefitsmaternidad',
    // Toggles de confirmación que además accionan beneficios/riders al recotizar:
    //  - step13Confirmation: sin él, useSteps sobreescribe `deductbl` a `deductbl_no`
    //    (pierde la reducción de deducible del template).
    //  - step14Confirmation: transformData filtra el rider `healthch` si es false
    //    (pierde el preventive checkup del template).
    //  - nonMaternidad: confirmación del disclaimer de maternidad; sin él la validación
    //    de avance bloquea y el check sale desmarcado.
    'step13Confirmation',
    'step14Confirmation',
    'nonMaternidad',
    // Sin esto useCoverageSelection descartaría un `dedusa_0` válido antes de que Step1
    // resuelva su propia query de países con deducible.
    'isCountryResidenceDeductible',
];

/**
 * @param {object} planData  El `data` guardado del plan favorito (payload de compute).
 * @returns {object} Payload para POPULATE_CONTEXT: sólo la configuración del plan.
 */
export const mapFavoritePlanToQuoteContext = (planData) => {
    if (!planData) return {};

    const formState = getPlanFormState(planData);

    const quoteContext = PLAN_CONFIGURATION_FIELDS.reduce((acc, field) => {
        // Se omiten las claves ausentes en vez de mandarlas como undefined: POPULATE_CONTEXT
        // hace merge sobre el estado, así que un plan al que le falte un campo conserva su
        // default en lugar de quedar en undefined.
        if (formState[field] !== undefined) acc[field] = formState[field];
        return acc;
    }, {});

    if (formState.country !== undefined) quoteContext.country = formState.country;

    return quoteContext;
};

export default mapFavoritePlanToQuoteContext;
